import { useRef } from 'react';
import {
  Avatar,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
} from '@locus-taxy/encore-mui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { getTestId, buildFormTestId } from '@locus-taxy/encore-utils';
import { PhotoCamera, Delete } from '@locus-taxy/encore-mui/icons';
import { useToast } from '@locus-taxy/encore-ui-toolkit';
import { TestIdAction } from '../../utils/testIdUtils';
import type { DocumentInfo } from '../../api/endpoints/useCurrentUser';
import { useFileUpload } from '../../api/endpoints/files/useFileUpload';
import { usePhotoUrl } from '../../hooks/usePhotoUrl';

interface GeneralSectionProps {
  fullName: string;
  emailAddress: string;
  photo?: DocumentInfo;
  onPhotoUpdate: (photo: DocumentInfo | null) => Promise<void>;
  isUpdating: boolean;
  canUpdate: boolean;
  clientId: string;
}

export function GeneralSection({
  fullName,
  emailAddress,
  photo,
  onPhotoUpdate,
  isUpdating,
  canUpdate,
  clientId,
}: GeneralSectionProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile, uploading } = useFileUpload(clientId);
  const { photoUrl, loading: photoLoading } = usePhotoUrl({
    fileId: photo?.fileId,
    clientId,
  });
  const { showError } = useToast();

  const handlePhotoChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError(t('userDetails.invalidFileType'));
      // Reset input value
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (1MB = 1048576 bytes)
    if (file.size > 1048576) {
      showError(t('userDetails.fileTooLarge'));
      // Reset input value
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    try {
      const documentInfo = await uploadFile(file);
      await onPhotoUpdate(documentInfo);
    } finally {
      // Reset input value to allow uploading the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemovePhoto = async () => {
    await onPhotoUpdate(null);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box
      sx={{
        borderRadius: 1,
        mb: 4,
        backgroundColor: 'white',
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('userDetails.general')}
        </Typography>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Box sx={{ position: 'relative' }}>
            {photoLoading ? (
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={40} />
              </Box>
            ) : (
              <Avatar src={photoUrl || undefined} sx={{ width: 80, height: 80 }} />
            )}
            {photoUrl && canUpdate && !photoLoading && (
              <IconButton
                size="small"
                onClick={handleRemovePhoto}
                disabled={isUpdating || uploading}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'error.dark',
                  },
                }}
                data-testid={getTestId({
                  action: TestIdAction.REMOVE_PHOTO,
                  object: 'button',
                  page: 'account',
                })}
              >
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handlePhotoChange}
              disabled={isUpdating || uploading || !canUpdate}
              data-testid={buildFormTestId({
                fieldType: 'file',
                fieldName: 'user-photo',
                entity: 'account',
                formAction: 'update',
              })}
            />
            <Button
              variant="outlined"
              startIcon={<PhotoCamera />}
              onClick={handleUploadClick}
              disabled={isUpdating || uploading || !canUpdate}
              data-testid={getTestId({
                action: TestIdAction.UPLOAD_PHOTO,
                object: 'button',
                page: 'account',
              })}
            >
              {uploading ? t('common.loading') : t('userDetails.changePicture')}
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 1 }}
            >
              {t('userDetails.photoDescription')}
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={2} maxWidth={600}>
          <TextField
            label={t('userDetails.fullName')}
            value={fullName}
            placeholder={t('userDetails.fullName')}
            fullWidth
            disabled
            data-testid={buildFormTestId({
              fieldType: 'text',
              fieldName: 'full-name',
              entity: 'account',
              formAction: 'view',
            })}
          />
          <TextField
            label={t('userDetails.emailAddress')}
            value={emailAddress}
            placeholder={t('userDetails.emailAddress')}
            fullWidth
            disabled
            data-testid={buildFormTestId({
              fieldType: 'text',
              fieldName: 'email-address',
              entity: 'account',
              formAction: 'view',
            })}
          />
        </Stack>
      </Box>
    </Box>
  );
}
